import { int } from '!/api'
import { model as router } from '!/router'
import {
  buildGuestRegionHeaders,
  getPrimaryRegionIdFromStorage,
} from '@/helpers'
import {
  createEvent,
  createStore,
  sample,
  StoreWritable,
  type EventCallable,
} from 'effector'
import { model as session } from '~/entities/session'
import { ERROR_CODE, PATH, STATUS } from '~/shared/constants'
import { T } from '~/shared/helpers'

export const init: EventCallable<void> = createEvent()
export const $isGeoblock = createStore<boolean | null>(null)
export const $isLaunchDataReady = createStore(false).on(
  int.launch.checkFx.finally,
  T
)

export const appCanBeLaunched = createEvent()

sample({
  clock: int.launch.checkFx.doneData,
  filter: (data) => {
    if (data.errorCode === ERROR_CODE.CL0002 && data.status === STATUS.ERROR) {
      return true // show geoblock page
    }
    return data.status === STATUS.OK
  },
  target: appCanBeLaunched,
})

export const $shouldTryToRestart: StoreWritable<boolean> = createStore(
  false
).on(int.launch.checkFx.fail, T)

sample({
  clock: int.launch.checkFx.doneData,
  filter: (data) => {
    return data.status === STATUS.ERROR && data.errorCode !== ERROR_CODE.CL0002
  },
  fn: T,
  target: $shouldTryToRestart,
})

// Set header only if guest has selected the region
sample({
  clock: init,
  source: {
    isNotAuthenticated: session.$isNotAuthenticated,
  },
  fn: ({ isNotAuthenticated }) =>
    buildGuestRegionHeaders({
      isNotAuthenticated,
      regionId: getPrimaryRegionIdFromStorage(),
    }),
  target: int.launch.checkFx,
})

sample({
  source: int.launch.checkFx.doneData,
  fn: (data) => data?.errorCode === ERROR_CODE.CL0002,
  target: $isGeoblock,
})

sample({
  source: { href: router.$href, isGeoblock: $isGeoblock },
  filter: ({ href, isGeoblock }) => {
    const isNoNeedToBlock =
      href === PATH.CREATORS ||
      href === PATH.ABOUT ||
      href === PATH.STATIC_SUPPORT ||
      href === PATH.TERMS_OF_USE ||
      href === PATH.STATIC_PRIVACY ||
      href === PATH.INDUSTRY ||
      href === PATH.GEOBLOCK
    return Boolean(isGeoblock) && !isNoNeedToBlock
  },
  fn: () => PATH.GEOBLOCK,
  target: router.navigatePush,
})

sample({
  source: {
    href: router.$href,
    isGeoblock: $isGeoblock,
    isLaunchDataReady: $isLaunchDataReady,
  },
  filter: ({ href, isGeoblock, isLaunchDataReady }) =>
    !isGeoblock && href === PATH.GEOBLOCK && isLaunchDataReady,
  fn: () => PATH.HOME,
  target: router.navigatePush,
})

export const $customErrorCode = createStore<string>('')

sample({
  clock: int.launch.checkFx.doneData,
  fn: (data) => data?.errorCode || '',
  target: $customErrorCode,
})
